/**
 * AppointmentFormModal — partagé par SEC_MEDICAL et SEC_IMAGERIE.
 *
 * Modes supportés :
 *   - mode="create" : crée un nouveau RDV (POST /appointments)
 *   - mode="edit"   : modifie un RDV existant (PATCH /appointments/{id})
 *
 * Props :
 *   - mode : "create" | "edit"
 *   - prefilledRequest   : (create only) — demande à rattacher via requestId
 *   - initialAppointment : (edit only) — RDV courant
 *   - lockedType         : si fourni, sélecteur de type caché et fixé (ex. SEC_IMAGERIE → "imagerie")
 *   - allowedTypes       : liste whitelist (défaut 5 types)
 *   - onClose, onSubmitted
 */
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Calendar, User, Search, X } from "lucide-react";

const ALL_TYPES = [
  { value: "consultation", label: "Consultation" },
  { value: "imagerie", label: "Imagerie" },
  { value: "analyses", label: "Analyses / Labo" },
  { value: "chimio_hdj", label: "Chimio / HDJ" },
  { value: "radiotherapie", label: "Radiothérapie" },
];

export default function AppointmentFormModal({
  mode = "create",
  prefilledRequest = null,
  initialAppointment = null,
  lockedType = null,
  allowedTypes = null,
  onClose,
  onSubmitted,
}) {
  const isEdit = mode === "edit";
  const appt = initialAppointment;

  const [search, setSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(
    isEdit
      ? { id: appt.patientId, firstName: appt.patientName?.split(" ")[0] || "", lastName: appt.patientName?.split(" ").slice(1).join(" ") || "" }
      : prefilledRequest
        ? { id: prefilledRequest.patientId, firstName: prefilledRequest.patientName?.split(" ")[0] || "", lastName: prefilledRequest.patientName?.split(" ").slice(1).join(" ") || "" }
        : null
  );
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState(isEdit ? (appt.doctorId || "") : "");
  const [type, setType] = useState(
    lockedType
      || (isEdit ? appt.type : null)
      || prefilledRequest?.type
      || "consultation"
  );
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState(isEdit ? (appt.location || "") : "");
  const [notes, setNotes] = useState(isEdit ? (appt.notes || "") : (prefilledRequest?.notes || ""));
  const [changeReason, setChangeReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const typeOptions = (allowedTypes || ALL_TYPES.map(t => t.value))
    .map(v => ALL_TYPES.find(t => t.value === v))
    .filter(Boolean);

  useEffect(() => {
    api.get("/users/clinic-members")
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : res.data.items || [];
        setDoctors(list.filter(u => u.role === "MEDECIN"));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      if (appt.date) {
        const d = new Date(appt.date);
        setDate(d.toISOString().slice(0, 10));
        setTime(d.toTimeString().slice(0, 5));
      }
      return;
    }
    const d = new Date();
    d.setDate(d.getDate() + 1);
    setDate(d.toISOString().slice(0, 10));
    setTime("10:00");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedPatient) return;
    if (search.length < 2) { setPatients([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/patients", { params: { search, limit: 10 } });
        setPatients(Array.isArray(res.data) ? res.data : res.data.items || []);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [search, selectedPatient]);

  const submit = async () => {
    if (!selectedPatient?.id) { toast.error("Sélectionnez un patient"); return; }
    if (!date || !time) { toast.error("Date et heure requises"); return; }
    setSubmitting(true);
    try {
      const iso = new Date(`${date}T${time}:00`).toISOString();
      if (isEdit) {
        const payload = {};
        if (iso !== new Date(appt.date).toISOString()) payload.date = iso;
        if (type !== appt.type) payload.type = type;
        if ((doctorId || null) !== (appt.doctorId || null)) payload.doctorId = doctorId || null;
        if ((location || null) !== (appt.location || null)) payload.location = location || null;
        if ((notes || null) !== (appt.notes || null)) payload.notes = notes || null;
        if (changeReason.trim()) payload.changeReason = changeReason.trim();
        if (Object.keys(payload).length === 0) {
          toast.info("Aucune modification à enregistrer");
          setSubmitting(false);
          return;
        }
        await api.patch(`/appointments/${appt.id}`, payload);
        toast.success("RDV modifié");
      } else {
        await api.post("/appointments", {
          patientId: selectedPatient.id,
          date: iso,
          type,
          doctorId: doctorId || null,
          location: location || null,
          notes: notes || null,
          requestId: prefilledRequest?.id || null,
          notifyPatient: notify,
        });
        toast.success("RDV créé et patient notifié");
      }
      onSubmitted?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="appointment-form-modal"
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[95vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-semibold text-slate-800 text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              {isEdit ? "Modifier le RDV" : "Nouveau RDV"}
              {lockedType && <span className="text-xs text-slate-400 ml-2">({lockedType})</span>}
            </h3>
            {!isEdit && prefilledRequest && (
              <p className="text-xs text-cyan-700 mt-0.5">Issu de la demande de {prefilledRequest.requesterName}</p>
            )}
          </div>
          <button onClick={onClose} data-testid="close-appt-form-btn" className="p-2 -mr-2 rounded-lg hover:bg-slate-100 min-w-[44px] min-h-[44px] flex items-center justify-center">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Patient */}
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Patient</label>
            {selectedPatient ? (
              <div className="flex items-center gap-3 px-3 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <User className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-emerald-800 truncate flex-1">{selectedPatient.firstName} {selectedPatient.lastName}</span>
                {!prefilledRequest && !isEdit && (
                  <button
                    onClick={() => { setSelectedPatient(null); setSearch(""); }}
                    className="text-sm text-slate-500 hover:text-red-600"
                    data-testid="change-patient-btn"
                  >
                    Changer
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un patient..."
                  data-testid="appt-form-patient-search"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px]"
                />
                {patients.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-xl mt-1 shadow-lg z-10 max-h-48 overflow-y-auto">
                    {patients.map(p => (
                      <button
                        key={p.id}
                        onClick={() => { setSelectedPatient(p); setPatients([]); setSearch(""); }}
                        data-testid={`appt-form-patient-option-${p.id}`}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 text-sm flex items-center gap-2 min-h-[44px]"
                      >
                        <User className="w-4 h-4 text-slate-400" />
                        {p.firstName} {p.lastName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Type (only if not locked) */}
          {!lockedType && (
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-2">Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {typeOptions.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setType(t.value)}
                    data-testid={`appt-form-type-${t.value}`}
                    className={`px-2 py-3 rounded-xl text-sm font-medium border-2 transition-all min-h-[44px] ${
                      type === t.value
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date & time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                data-testid="appt-form-date"
                className="w-full px-3 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Heure</label>
              <input
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                data-testid="appt-form-time"
                className="w-full px-3 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px]"
              />
            </div>
          </div>

          {/* Doctor */}
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Médecin (optionnel)</label>
            <select
              value={doctorId}
              onChange={e => setDoctorId(e.target.value)}
              data-testid="appt-form-doctor"
              className="w-full px-3 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px]"
            >
              <option value="">-- Aucun --</option>
              {doctors.map(d => (
                <option key={d.id} value={d.id}>Dr. {d.firstName} {d.lastName}</option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Lieu (optionnel)</label>
            <input
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Ex: Salle 3, Radio A..."
              data-testid="appt-form-location"
              className="w-full px-3 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px]"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-slate-600 block mb-1">Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              data-testid="appt-form-notes"
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {isEdit && (
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Motif de modification (optionnel, visible dans l'historique)</label>
              <textarea
                value={changeReason}
                onChange={e => setChangeReason(e.target.value)}
                rows={2}
                data-testid="appt-form-change-reason"
                placeholder="Ex : patient a demandé un report"
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          )}

          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={notify}
                onChange={e => setNotify(e.target.checked)}
                data-testid="appt-form-notify"
                className="w-4 h-4 accent-emerald-600"
              />
              Notifier le patient (push)
            </label>
          )}
        </div>

        <div className="p-5 border-t bg-slate-50 flex gap-3 sticky bottom-0">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 py-3 text-slate-600 hover:bg-slate-200 rounded-xl font-medium min-h-[44px]"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={submitting || !selectedPatient?.id}
            data-testid="submit-appt-form"
            className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors min-h-[44px]"
          >
            {submitting ? (isEdit ? "Enregistrement..." : "Création...") : (isEdit ? "Enregistrer" : "Créer le RDV")}
          </button>
        </div>
      </div>
    </div>
  );
}
